/**
 * HandHistory — Displays actions grouped by betting round
 *
 * Shows community cards at each stage transition and auto-scrolls
 * to the latest action.
 *
 * Requirements: 19.1, 19.2, 19.3, 19.4, 19.5
 */

import React, { useEffect, useRef } from 'react';
import { HandHistoryProps, ActionRecord, HandPhase } from '../types';
import { CardDisplay } from './CardDisplay';
import './HandHistory.css';

const PHASE_LABELS: Record<string, string> = {
  'pre-flop': 'Pre-Flop',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
  showdown: 'Showdown',
};

function formatAction(action: ActionRecord): { label: string; cssClass: string } {
  switch (action.action) {
    case 'fold':
      return { label: 'Folds', cssClass: 'poker-history-action__type--fold' };
    case 'check':
      return { label: 'Checks', cssClass: 'poker-history-action__type--check' };
    case 'call':
      return { label: `Calls ${action.amount}`, cssClass: 'poker-history-action__type--call' };
    case 'raise':
      return { label: `Raises ${action.amount}`, cssClass: 'poker-history-action__type--raise' };
    case 'all-in':
      return { label: `All-In ${action.amount}`, cssClass: 'poker-history-action__type--all-in' };
    default:
      return { label: action.action, cssClass: '' };
  }
}

export const HandHistory: React.FC<HandHistoryProps> = ({
  actions,
  communityCards,
  currentPhase,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [actions.length]);

  // Group actions by phase
  const phases: HandPhase[] = ['pre-flop', 'flop', 'turn', 'river', 'showdown'];
  const groupedActions: Record<string, ActionRecord[]> = {};
  for (const phase of phases) {
    groupedActions[phase] = actions.filter((a) => a.phase === phase);
  }

  const activePhasesWithActions = phases.filter(
    (phase) => groupedActions[phase].length > 0
  );

  return (
    <div className="poker-hand-history" ref={scrollRef} role="log" aria-label="Hand history">
      <h3 className="poker-hand-history__title">Hand History</h3>

      {activePhasesWithActions.length === 0 ? (
        <div className="poker-hand-history__empty">No actions yet</div>
      ) : (
        activePhasesWithActions.map((phase) => (
          <div key={phase} className="poker-history-round">
            <div className="poker-history-round__header">
              {PHASE_LABELS[phase] || phase}
            </div>

            {/* Show community cards for flop/turn/river */}
            {phase === 'flop' && communityCards.flop.length > 0 && (
              <div className="poker-history-round__cards">
                {communityCards.flop.map((card, i) => (
                  <CardDisplay key={`flop-${i}`} card={card} size="small" />
                ))}
              </div>
            )}
            {phase === 'turn' && communityCards.turn && (
              <div className="poker-history-round__cards">
                <CardDisplay card={communityCards.turn} size="small" />
              </div>
            )}
            {phase === 'river' && communityCards.river && (
              <div className="poker-history-round__cards">
                <CardDisplay card={communityCards.river} size="small" />
              </div>
            )}

            {groupedActions[phase].map((action, idx) => {
              const { label, cssClass } = formatAction(action);
              return (
                <div key={`${phase}-${idx}`} className="poker-history-action">
                  <span className="poker-history-action__player">
                    {action.playerName}
                  </span>
                  <span className={`poker-history-action__type ${cssClass}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

export default HandHistory;
